import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Dashboard
import DashboardPage from './pages/DashboardPage';

// Clients & Services
import ClientsPage from './pages/ClientsPage';
import ServicesPage from './pages/ServicesPage';
import ArticlesPage from './pages/ArticlesPage';
import TemplatesPage from './pages/TemplatesPage';
import usePageTracking from './hooks/usePageTracking';

// Tracking wrapper — must live INSIDE <BrowserRouter> to access useLocation.
const PageTracker = () => { usePageTracking(); return null; };

// Invoices
import InvoicesPage from './pages/invoices/InvoicesPage';
import InvoiceFormPage from './pages/invoices/InvoiceFormPage';
import InvoiceDetailPage from './pages/invoices/InvoiceDetailPage';

// Quotes
import QuotesPage from './pages/quotes/QuotesPage';
import QuoteFormPage from './pages/quotes/QuoteFormPage';

// Delivery Notes
import DeliveryNotesPage from './pages/delivery/DeliveryNotesPage';
import DeliveryNoteFormPage from './pages/delivery/DeliveryNoteFormPage';

// History
import HistoryPage from './pages/HistoryPage';

// Reports
import ReportsPage from './pages/ReportsPage';

// AI Assistant
import AuxiliarPage from './pages/AuxiliarPage';

// Expenses
import ExpensesPage from './pages/ExpensesPage';
import ExpenseInboxPage from './pages/ExpenseInboxPage';

// Contracts
import ContractsPage from './pages/ContractsPage';

// Bulk Upload
import BulkUploadPage from './pages/BulkUploadPage';

// Help
import HelpPage from './pages/HelpPage';

// Admin
import AdminCompaniesPage from './pages/AdminCompaniesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminDemoRequestsPage from './pages/AdminDemoRequestsPage';
import ProformasPage from './pages/proformas/ProformasPage';
import ProformaFormPage from './pages/proformas/ProformaFormPage';
import ClientDetail360Page from './pages/clients/ClientDetail360Page';
import InspeccionPage from './pages/InspeccionPage';

// Settings
import SettingsPage from './pages/SettingsPage';

// Calendar
import CalendarPage from './pages/CalendarPage';

// Providers
import ProvidersPage from './pages/ProvidersPage';

// Banking
import BankingPage from './pages/BankingPage';

// CashFlow
import CashFlowPage from './pages/CashFlowPage';

// Landing
import LandingPage from './pages/LandingPage';

// Legal
import LegalPage from './pages/LegalPage';

// Tax
import TaxPage from './pages/TaxPage';

// Client Portal (public)
import ClientPortalPage from './pages/ClientPortalPage';

// Payment (public)
import PaymentPage from './pages/PaymentPage';

// Receivables + Dunning
import ReceivablesPage from './pages/ReceivablesPage';

// Banking PSD2
import BankingPsd2Page from './pages/BankingPsd2Page';

// Portal Magic Link
import PortalMagicPage, { PortalMagicExchangePage } from './pages/PortalMagicPage';

// Subscriptions
import SubscriptionsPage from './pages/SubscriptionsPage';

// Projects + Time tracking + Profitability
import ProjectsPage from './pages/ProjectsPage';

// Tax Forecasting
import TaxForecastPage from './pages/TaxForecastPage';

// AI Advisor (Claude)
import AiAdvisorPage from './pages/AiAdvisorPage';

// Bulk import
import ImportPage from './pages/ImportPage';

// Export center (paquetes para el gestor)
import ExportCenterPage from './pages/ExportCenterPage';

import './App.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DashboardPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ClientsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/services"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ServicesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/articles"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ArticlesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TemplatesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Invoices */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <InvoicesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <InvoiceFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <InvoiceDetailPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id/edit"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <InvoiceFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Quotes */}
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <QuotesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <QuoteFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <QuoteFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Delivery Notes */}
          <Route
            path="/delivery-notes"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DeliveryNotesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/delivery-notes/new"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DeliveryNoteFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Expenses */}
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ExpensesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/inbox"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ExpenseInboxPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Contracts */}
          <Route
            path="/contracts"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContractsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Bulk Upload */}
          <Route
            path="/invoices/import"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <BulkUploadPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Help */}
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <HelpPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Calendar */}
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CalendarPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Providers */}
          <Route
            path="/providers"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProvidersPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Banking */}
          <Route
            path="/banking"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <BankingPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* CashFlow */}
          <Route
            path="/cashflow"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CashFlowPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Tax */}
          <Route
            path="/tax"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TaxPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AdminCompaniesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AnalyticsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/demo-requests"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AdminDemoRequestsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/proformas"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProformasPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/proformas/new"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProformaFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/proformas/:id/edit"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProformaFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients/:id/360"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ClientDetail360Page />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inspeccion"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <InspeccionPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* History */}
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <HistoryPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Reports */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ReportsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* AI Assistant */}
          <Route
            path="/auxiliar"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AuxiliarPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Settings */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Landing & Redirect */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacidad" element={<LegalPage />} />
          <Route path="/c/:token" element={<ClientPortalPage />} />
          <Route path="/portal" element={<PortalMagicPage />} />
          <Route path="/c/magic/:magicToken" element={<PortalMagicExchangePage />} />
          <Route path="/pay/:token" element={<PaymentPage />} />
          <Route path="/pay/:token/success" element={<PaymentPage />} />
          <Route
            path="/receivables"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ReceivablesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/banking-psd2"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <BankingPsd2Page />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SubscriptionsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProjectsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tax/forecast"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TaxForecastPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-advisor"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AiAdvisorPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/import"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ImportPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/export-center"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ExportCenterPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
